import { useState, type FormEvent } from 'react'
import { Avatar } from '@/components/Avatar'
import { StatusIcon } from '@/components/StatusIcon'
import { STATUS_LABEL } from '@/lib/presence'
import { avatarUrl, guildIconUrl, memberAvatarUrl, userBannerUrl } from '@/lib/avatar'
import { useProfileBanner } from '@/lib/profileBanner'
import { displayName } from '@/store/social'
import { guildColor, guildInitials } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import { effectSources, themeVars } from '@/lib/decor'
import type { Guild, Member, PresenceStatus, Profile, User } from '@/types/api'
import type { Activity } from '@/store/social'
import styles from './ProfileCard.module.css'
import { StyledName } from '../StyledName'
import { ServerTag } from '../ServerTag'
import { AchievementIcon } from '../AchievementIcon'
import { BadgeIcon } from './BadgeIcon'
import { Tooltip } from '../Tooltip'

/** Вкладки большого окна профиля: общее бывает только с другими, профиль на сервере — только свой. */
export type ProfileTab = 'activity' | 'server' | 'friends' | 'guilds'

interface Props {
  user: User
  /** Участник текущего сервера: ник и роли берём отсюда. */
  member?: Member | undefined
  guild?: Guild | undefined
  /** Серверы пользователя: по ним раскрываем общие. */
  guilds: Record<string, Guild>
  profile?: Profile | undefined
  status: PresenceStatus | undefined
  /** Во что играет прямо сейчас; пусто — ничего не показываем. */
  activity?: Activity | null | undefined
  /** Свой статус текстом; пусто — не задан или человек не в сети. */
  customStatus?: string | undefined
  /** Чем занят, если не игрой (боты): «Слушает радио». */
  activityLine?: string | undefined
  isMe: boolean
  canAddFriend: boolean
  busy: boolean
  error: string | null
  onOpenDM: () => void
  onSend: (text: string) => void
  onAddFriend: () => void
  /** Открыть большое окно профиля на нужной вкладке; без него общее не кликается. */
  onOpenMutuals?: ((tab: ProfileTab) => void) | undefined
  /** Поле «Сообщение для …» — только во всплывающей карточке. */
  showSend?: boolean
  /** Карточка в панели личной беседы: «Написать» там лишнее — беседа уже открыта. */
  inDM?: boolean
}

function date(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** Склонение: 1 сервер, 2 сервера, 5 серверов. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

/** Карточка профиля: баннер, аватар, имя, общее с вами, роли и поле для сообщения. */
export function ProfileCard(p: Props) {
  const [draft, setDraft] = useState('')
  const name = p.member?.nick ?? (p.user.username ? displayName(p.user) : p.user.id)
  const banner = userBannerUrl(p.user.id, p.profile?.banner)
  const avatar = memberAvatarUrl(p.guild?.id, p.member ?? { avatar: null, user: p.user })
  const bannerStyle = useProfileBanner(banner, avatar)
  const roles = (p.member?.roles ?? [])
    .map((id) => p.guild?.roles.find((r) => r.id === id))
    // у @everyone идентификатор совпадает с сервером — её в чипах не показываем
    .filter((r): r is NonNullable<typeof r> => Boolean(r) && r!.id !== p.guild?.id)
    .sort((a, b) => b.position - a.position)
  const mutualGuilds = (p.profile?.mutual_guild_ids ?? []).map((id) => p.guilds[id]).filter((g): g is Guild => Boolean(g))
  const mutualFriends = p.profile?.mutual_friends ?? []
  const status = p.status ?? 'offline'

  const send = (e: FormEvent) => {
    e.preventDefault()
    const text = draft.trim()
    if (!text) return
    setDraft('')
    p.onSend(text)
  }

  const effect = p.profile?.profile_effect ? effectSources(p.profile.profile_effect.id) : null
  const theme = themeVars(p.profile?.theme_colors)
  const badges = p.profile?.badges ?? []
  const showcase = p.profile?.achievements_showcase ?? []
  return (
    <div className={cx(styles.card, effect && styles.withEffect, theme && styles.themed)} style={theme}>
      {/* эффект профиля — под всем содержимым карточки, снизу вверх гаснет */}
      {effect ? <img className={styles.effect} src={effect.src} srcSet={effect.srcSet} sizes="360px" alt="" aria-hidden="true" draggable={false} /> : null}
      <div className={styles.banner} style={bannerStyle} />
      <div className={styles.avatarWrap}>
        {/* открыт с сервера — аватар для этого сервера, если он есть */}
        <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={88} src={avatar} className={styles.avatar} decoration={p.user.avatar_decoration_data} />
        <span className={styles.status} data-tooltip={STATUS_LABEL[status]}>
          <StatusIcon status={status} size={22} label={STATUS_LABEL[status]} />
        </span>
      </div>
      {/* значки; Плюс — только здесь, рядом с именем он стоял вторым разом */}
      {badges.length ? (
        <div className={styles.badges}>
          {badges.map((b) => <BadgeIcon key={b.id} id={b.id} name={b.name} />)}
        </div>
      ) : null}

      <div className={styles.body}>
        <div className={styles.names}>
          <StyledName name={name} style={p.user.display_name_styles} className={styles.display} />
          {p.user.primary_guild ? <ServerTag tag={p.user.primary_guild.tag} color={p.user.primary_guild.color} /> : null}
          {p.user.bot ? <span className={styles.bot}>бот</span> : null}
        </div>
        {p.user.username ? <div className={styles.username}>@{p.user.username}</div> : null}
        {p.customStatus ? <div className={styles.custom} data-tooltip={p.customStatus}>{p.customStatus}</div> : null}
        {!p.activity && p.activityLine ? <div className={styles.custom} data-tooltip={p.activityLine}>{p.activityLine}</div> : null}
        {p.activity ? (
          <div className={styles.playing}>
            {p.activity.steam ? (
              // обложка идёт через наш сервер: и политика безопасности сайта, и раздаче Steam
              // незачем знать, кто на кого смотрит
              <img
                className={styles.cover}
                src={`/api/v1/games/cover?steam=${encodeURIComponent(p.activity.steam)}`}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            ) : null}
            <span className={styles.playingText}>
              <span className={styles.playingLabel}>Сейчас играет в</span>
              <b>{p.activity.name}</b>
            </span>
          </div>
        ) : null}

        <div className={styles.panel}>
          {mutualFriends.length || mutualGuilds.length ? (
            <div className={styles.mutuals}>
              {mutualFriends.length ? (
                <button type="button" className={cx(styles.mutual, p.onOpenMutuals && styles.mutualLink)} disabled={!p.onOpenMutuals} onClick={() => p.onOpenMutuals?.('friends')}>
                  <span className={styles.faces}>
                    {mutualFriends.slice(0, 3).map((f) => (
                      <Avatar key={f.id} initial={displayName(f).slice(0, 1).toUpperCase()} color="#5865F2" size={20} src={avatarUrl(f.id, f.avatar)} className={styles.face} />
                    ))}
                  </span>
                  {plural(mutualFriends.length, 'общий друг', 'общих друга', 'общих друзей')}
                </button>
              ) : null}
              {mutualGuilds.length ? (
                <button type="button" className={cx(styles.mutual, p.onOpenMutuals && styles.mutualLink)} disabled={!p.onOpenMutuals} onClick={() => p.onOpenMutuals?.('guilds')}>
                  <span className={styles.faces}>
                    {mutualGuilds.slice(0, 3).map((g) => {
                      const icon = guildIconUrl(g.id, g.icon)
                      return icon
                        ? <img key={g.id} src={icon} alt="" className={styles.face} />
                        : <span key={g.id} className={cx(styles.face, styles.faceText)} style={{ background: guildColor(g.id) }}>{guildInitials(g.name)}</span>
                    })}
                  </span>
                  {plural(mutualGuilds.length, 'общий сервер', 'общих сервера', 'общих серверов')}
                </button>
              ) : null}
            </div>
          ) : null}

          {showcase.length ? (
            <div className={styles.section}>
              <div className={styles.label}>Достижения</div>
              <div className={styles.showcase}>
                {showcase.map((a) => (
                  <Tooltip
                    key={a.id}
                    className={styles.showcaseItem}
                    content={
                      <span className={styles.achievementTip}>
                        <span className={styles.achievementTipName}>{a.name}</span>
                        <span className={styles.achievementTipText}>{a.description}</span>
                      </span>
                    }
                  >
                    <AchievementIcon id={a.id} size={34} />
                    <span className={styles.showcaseName}>{a.name}</span>
                  </Tooltip>
                ))}
              </div>
            </div>
          ) : null}

          {p.profile?.bio ? (
            <div className={styles.section}>
              <div className={styles.label}>О себе</div>
              <p className={styles.bio}>{p.profile.bio}</p>
            </div>
          ) : null}

          {roles.length ? (
            <div className={styles.section}>
              <div className={styles.label}>Роли на сервере</div>
              <div className={styles.roles}>
                {roles.map((r) => (
                  <span key={r.id} className={styles.role}>
                    <span className={styles.roleDot} style={{ background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : 'var(--interactive-muted)' }} />
                    {r.name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className={styles.dates}>
            {p.member?.joined_at ? <span>На сервере с {date(p.member.joined_at)}</span> : null}
            {p.profile ? <span>В Кличате с {date(p.profile.created_at)}</span> : null}
          </div>
        </div>

        {p.isMe ? (
          // своя карточка: с сервера — сразу к нику и аватару для него, иначе — к «Активности»
          p.onOpenMutuals ? (
            <div className={styles.actions}>
              <button type="button" className={cx(styles.action, styles.actionSecondary)} onClick={() => p.onOpenMutuals?.(p.guild ? 'server' : 'activity')}>
                {p.guild ? 'Профиль на сервере' : 'Открыть профиль'}
              </button>
            </div>
          ) : null
        ) : (
          <>
            {p.canAddFriend ? (
              <div className={styles.actions}>
                <button type="button" className={styles.action} disabled={p.busy} onClick={p.onAddFriend}>Добавить в друзья</button>
                {p.inDM ? null : <button type="button" className={cx(styles.action, styles.actionSecondary)} disabled={p.busy} onClick={p.onOpenDM}>Написать</button>}
              </div>
            ) : null}
            {p.showSend === false ? null : (
            <form onSubmit={send}>
              <input
                className={styles.send}
                placeholder={p.user.username ? `Сообщение для @${p.user.username}` : 'Сообщение'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={p.busy}
                aria-label="Сообщение"
              />
            </form>
            )}
          </>
        )}
        {p.error ? <p className={styles.error} role="alert">{p.error}</p> : null}
      </div>
    </div>
  )
}
